/**
 *  @class
 *  @function Predictive Search
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');

      this.setupEventListeners();

    }
    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      // ADO #1081: only close when focus leaves the search form, so keyboard users can move between results.
      this.predictiveSearchResults.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) {
          this.close();
        }
      });
      this.addEventListener('keydown', this.onKeydown.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.cc.addEventListener('click', this.close.bind(this));

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        }
      });
      this.input.addEventListener('focus', this.open.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    // Arrow keys move between the input and the result links.
    onKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }
      if (event.target !== this.input && !this.predictiveSearchResults.contains(event.target)) {
        return;
      }
      if (this.predictiveSearchResults.hasAttribute('inert')) {
        return;
      }
      const links = Array.from(this.predictiveSearchResults.querySelectorAll('a[href]:not([tabindex="-1"]), #search-results-submit'))
        .filter((el) => el.offsetParent !== null);
      if (!links.length) {
        return;
      }
      const current = links.indexOf(document.activeElement);
      let next;
      if (event.key === 'ArrowDown') {
        next = current === -1 ? 0 : Math.min(current + 1, links.length - 1);
      } else {
        next = current - 1;
      }
      event.preventDefault();
      if (next < 0) {
        this.input.focus();
      } else {
        links[next].focus();
      }
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        document.body.classList.remove('open-search');
        this.predictiveSearchResults.setAttribute('inert', '');
        return;
      }
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
      this.getSearchResults(searchTerm);
    }

    // The store appends `tag:searchable` to every query, so strip any
    // field:value operator before comparing against a vendor name.
    cleanQuery(value) {
      return (value || '')
        .split(' ')
        .filter((word) => word.indexOf(':') === -1)
        .join(' ')
        .trim()
        .toLowerCase();
    }

    matchVendor(value) {
      const typed = this.cleanQuery(value);

      if (!typed) {
        return null;
      }

      if (!this.vendors) {
        const source = this.querySelector('.brand-search-vendors');

        try {
          this.vendors = source ? JSON.parse(source.textContent) : [];
        } catch (error) {
          this.vendors = [];
        }
      }

      return this.vendors.find((vendor) => String(vendor).trim().toLowerCase() === typed) || null;
    }

    brandUrl(vendor) {
      return (
        this.dataset.brandUrlBase +
        encodeURIComponent(vendor) +
        (this.dataset.brandUrlSuffix || '')
      );
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
        return;
      }

      // A bare brand name goes to that brand's collection instead of /search,
      // which would otherwise be narrowed by the tag filter.
      const vendor = this.matchVendor(this.input.value);

      if (vendor) {
        event.preventDefault();
        window.location.assign(this.brandUrl(vendor));
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        return;
      }

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {

      this.predictiveSearchResults.classList.add('loading');

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,collection&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then((response) => {
          this.predictiveSearchResults.classList.remove('loading');
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      // A bare brand name renders nothing — it goes straight to the brand
      // collection on submit, so keep the panel shut rather than flash an
      // empty box.
      if (!resultsMarkup.trim()) {
        this.close();
        return;
      }

      let submitButton = this.querySelector('#search-results-submit');

      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    close() {
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }
  customElements.define('search-form', SearchForm);
}